import {assert} from 'Asserts';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {
  getParentStorablesByRowId,
  makeParentStorableCapacityTableValidation,
} from 'notebook/integrations/samples/InventoryCapacityValidationFunctions';
import * as ParentStorableCustomEval from 'notebook/integrations/samples/transfer-table-columns/ParentStorableCustomEval';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  BaseStructuredTable,
  CreateStructuredTableProps,
  DataDependentTableValidation,
  EvalTableView,
  GetCustomEvalArgsOptions,
  StructuredTableColumn,
  StructuredTableMetadata,
} from 'notebook/structured-tables/StructuredTableConstants';
import {CustomEvalFnAndType, CustomEvalType} from 'notebook/tables/constants/CustomEvalConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {CellIndex} from 'notebook/tables/constants/TableSelectionConstants';
/**
 * A base adapter class for grid creation tables. Contains all logic shared between plate & box creation
 * tables, with additional specific logic contained in their respective adapters.
 */

/**
 * Agent keep in sync: benchling/notebook/structured_tables/constants/grid_creation_tables.py
 */
export interface GridCreationTableMetadata {
  // if false, then we will show a column where the barcode can be entered
  shouldAutogenerateBarcode: boolean;
  // If false, then we will show a column that is read-only with placeholder text. This property is missing in tables that were created before we offered the option for a custom name.
  requireCustomName?: boolean;
  // Should always be false. In fact, this property should be moved to the column:
  // TODO(BNCH-12025) Move to StructuredTableColumn
  parentStorableIsGrid: boolean;
}

/**
 * We don't have any need for any metadata on the row, so our TRowMetadata is just an empty
 * object. The typedef isn't necessary, but it's more explicit than "{}"
 */
export interface GridCreationTableRowMetadata extends RowMetadata {}

interface GridCreationTableColumnCustomEvalInfo extends CustomEvalFnAndType<{
  tableMetadata: StructuredTableMetadata & GridCreationTableMetadata;
  rowMetadata: GridCreationTableRowMetadata;
}> {
  getColumnIndexesOfDependencies(columns: ReadonlyArray<StructuredTableColumn>): Array<number>;
}

// Agent keep in sync: benchling/notebook/structured_tables/instantiators/inventory_table_instantiator.py
export abstract class BaseGridCreationStructuredTableAdapter extends GenericStructuredTableAdapter<
  GridCreationTableMetadata,
  GridCreationTableRowMetadata
> {
  override createTable(
    createProps: CreateStructuredTableProps<GridCreationTableMetadata>
  ): Omit<GridCreationStructuredTable, 'cid'> {
    assert(
      createProps.metadata.parentStorableIsGrid != null && !createProps.metadata.parentStorableIsGrid,
      'Grid creation table must have `parentStorableIsGrid` set to false.',
      {createProps}
    );
    return super.createTable(createProps);
  }

  private getColumnCustomEvalInfo(
    tableMetadata: GridCreationTableMetadata,
    column: StructuredTableColumn
  ): GridCreationTableColumnCustomEvalInfo | null {
    if (column.columnType === TableColumnType.PARENT_STORABLE) {
      return {
        customEvalFn: ParentStorableCustomEval.validateParentStorableCapacityCustomEval(),
        customEvalType: CustomEvalType.DEFAULT,
        getColumnIndexesOfDependencies: (columns) => [],
      };
    }
    return null;
  }

  override getTableSpecificCustomEvalFn(
    tableView: EvalTableView<GridCreationTableMetadata, GridCreationTableRowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: GridCreationTableMetadata;
    rowMetadata: GridCreationTableRowMetadata;
  }> | null {
    const {column, row} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    const {metadata} = tableView;
    if (row.isFinalized) {
      return null;
    }

    const customEvalInfo = this.getColumnCustomEvalInfo(metadata, column);
    if (!customEvalInfo) {
      return null;
    }
    return {
      customEvalFn: customEvalInfo.customEvalFn,
      customEvalType: customEvalInfo.customEvalType,
    };
  }

  override getTableSpecificCustomEvalArgs(
    tableView: EvalTableView<GridCreationTableMetadata, GridCreationTableRowMetadata>,
    cellIndex: CellIndex,
    options: GetCustomEvalArgsOptions
  ): Array<string> {
    const {toSpreadsheetIndex} = options;
    const {columns, metadata} = tableView;
    const {row: currentRowIndex, column: currentColumnIndex} = cellIndex;
    const dependencyColumnIndexes =
      this.getColumnCustomEvalInfo(metadata, columns[currentColumnIndex])?.getColumnIndexesOfDependencies(
        columns
      ) ?? [];

    return dependencyColumnIndexes.map((dependencyIndex, slotIndex) => {
      assert(
        dependencyIndex !== -1,
        `Could not find dependency at slot ${slotIndex} for column at index ${currentColumnIndex}`
      );
      return `=${toSpreadsheetIndex({row: currentRowIndex, column: dependencyIndex})}`;
    });
  }

  private readonly parentStorableCapacityValidation = makeParentStorableCapacityTableValidation(
    (structuredTable: GridCreationStructuredTable, computedTable) =>
      getParentStorablesByRowId({structuredTable, computedTable})
  );

  override getDataDependentTableValidation(): DataDependentTableValidation<
    GridCreationTableMetadata,
    GridCreationTableRowMetadata
  > {
    return this.parentStorableCapacityValidation;
  }

  /**
   * We don't need to extend the rows with any additional metadata, so
   * we always return an empty object here.
   */
  getBlankRowMetadata(tableMetadata: GridCreationTableMetadata): GridCreationTableRowMetadata {
    return {};
  }

  override allowMergedCellRangeInColumn(
    structuredTable: GridCreationStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    return ![TableColumnType.PLATE, TableColumnType.BOX, TableColumnType.STRING].includes(column.columnType);
  }

  override isReadOnly(): boolean {
    return !isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE});
  }
}

export type GenericGridCreationStructuredTable<TIds> = BaseStructuredTable<
  GridCreationTableMetadata,
  GridCreationTableRowMetadata,
  TIds
>;

export type GridCreationStructuredTable = GenericGridCreationStructuredTable<ClientId>;
export type FromServerPlateCreationStructuredTable = GenericGridCreationStructuredTable<FromServerId>;
export type ToServerPlateCreationStructuredTable = GenericGridCreationStructuredTable<ToServerId>;
